import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import { BrowserRouter, StaticRouter, Routes, Route, Link, useLocation } from 'react-router-dom';

// Per-page titles and descriptions. The build-time prerender script uses these too.
export const pageMeta = {
  '/': {
    title: 'Adeez - Custom Software Development | AI & Data Solutions | Noida, India',
    description: 'Adeez is a software development company in Noida, India. We build custom software, AI/ML solutions, mobile apps, and data analytics for clients worldwide.'
  },
  '/about': {
    title: 'About Adeez | Software Development Team in Noida, India',
    description: 'Meet the team behind Adeez and learn how we work with clients around the world.'
  },
  '/portfolio': {
    title: 'Portfolio | Adeez Software Projects',
    description: 'Selected software projects built by the Adeez team across web, mobile, AI and data.'
  },
  '/pricing': {
    title: 'Pricing | Adeez Software Development',
    description: 'Transparent pricing for MVPs, full products, dedicated developers and support plans.'
  }
};

// Keeps the browser tab title and meta description in sync with the current page.
const PageMeta = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const meta = pageMeta[pathname] || pageMeta['/'];
    document.title = meta.title;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', meta.description);
  }, [pathname]);
  return null;
};
import { 
  Code2, Wrench, Users, ArrowRight, Mail, Search, PenTool, ShieldCheck, 
  CheckCircle, Zap, Shield, Rocket, TrendingUp, Sun, Moon, 
  Award, Brain, Smartphone, Heart, ShoppingCart, Building2, Banknote, 
  Quote, Phone, MapPin, Send, ChevronLeft, ChevronRight, Star, Play,
  BarChart3, PieChart, Database, LineChart, GraduationCap, Layers,
  Globe, DollarSign, Clock, MessageCircle, Menu, X, Loader2
} from 'lucide-react';

// Lazy load page components
const About = lazy(() => import('./pages/About'));
const Portfolio = lazy(() => import('./pages/Portfolio'));
const Pricing = lazy(() => import('./pages/Pricing'));

// Loading Skeleton Component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <Loader2 className="w-12 h-12 animate-spin text-purple-500 mx-auto mb-4" />
      <p className="text-gray-500">Loading...</p>
    </div>
  </div>
);

// Lazy Image Component with placeholder
const LazyImage = ({ src, alt, className, ...props }) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  
  return (
    <div className={`relative ${className}`} {...props}>
      {!loaded && !error && (
        <div className="absolute inset-0 bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-800 animate-pulse rounded-inherit" />
      )}
      <img
        src={src}
        alt={alt}
        loading="lazy"
        onLoad={() => setLoaded(true)}
        onError={() => setError(true)}
        className={`${className} ${loaded ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}
        style={{ ...props.style }}
      />
    </div>
  );
};

// ScrollToTop component - scrolls to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

const AdeezLogo = ({ className = "h-8", darkMode = true }) => (
  <span className="inline-flex items-center gap-2" aria-label="Adeez">
    <img src="/logo-mark.png" alt="" className={className} width="32" height="32" />
    <span className={`text-xl font-bold tracking-[0.18em] ${darkMode ? 'text-white' : 'text-gray-900'}`}>ADEEZ</span>
  </span>
);


// const handleFormSubmit = async (e) => {
//   e.preventDefault();

//   const payload = {
//     name: formData.name,
//     email: formData.email,
//     phone: `${formData.countryCode}${formData.phone}`,
//     company: formData.company,
//     website: formData.website,
//     projectType: formData.projectType,
//     budget: formData.budget,
//     timeline: formData.timeline,
//     message: formData.message,
//     agreeNda: formData.agreeNda,
//     wantGuide: formData.wantGuide,
//   };

//   try {
//     const response = await fetch("https://script.google.com/macros/s/AKfycbyhgprRrA7b807aoxjHcvP4-Ly3CdJRZPcRLh_nLOcRbqWB9gw-ZXjOqKVLF2mZzCkD/exec", {
//       method: "POST",
//       body: JSON.stringify(payload),
//       headers: {
//         "Content-Type": "application/json"
//       }
//     });

//     if (response.ok) alert("Form submitted!");
//   } catch {
//     alert("Submit failed");
//   }
// };


// Testimonials removed until there are real, attributable client quotes.

// Removed fake client logos - will add real ones when available

const services = [
  { 
    icon: Code2, 
    title: 'Custom Software Development', 
    desc: 'We build tailored web applications that solve your specific business challenges—from CRM systems to SaaS platforms.', 
    features: [
      'Full-stack development (React, Node.js, Python)',
      'Cloud-native architecture (AWS, Azure, GCP)',
      'Third-party API integration & custom APIs',
      'Database design & optimization',
      'Microservices architecture'
    ], 
    priceUSD: 15000,
    priceType: 'starting',
    timeline: '8-12 weeks typical',
    cta: 'Get a Quote',
    image: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=400&h=300&fit=crop' 
  },
  { 
    icon: Brain, 
    title: 'AI & ML Solutions', 
    desc: 'Leverage artificial intelligence to automate processes, gain insights, and create intelligent features your competitors don\'t have.', 
    features: [
      'Predictive analytics & forecasting models',
      'Natural language processing (chatbots, sentiment)',
      'Computer vision (image recognition, OCR)',
      'Recommendation engines',
      'LLM integration (ChatGPT, Claude, custom)'
    ], 
    priceUSD: 10000,
    priceType: 'starting',
    timeline: '6-10 weeks typical',
    cta: 'Get a Quote',
    image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=400&h=300&fit=crop' 
  },
  { 
    icon: BarChart3, 
    title: 'Data Analytics & BI', 
    desc: 'Turn your data into actionable insights with custom dashboards and real-time reporting systems.', 
    features: [
      'Interactive dashboards (Power BI, Tableau, custom)',
      'Real-time data pipelines & ETL processes',
      'Predictive analytics & business forecasting',
      'Data warehouse design & implementation',
      'KPI tracking & automated reporting'
    ], 
    priceUSD: 8000,
    priceType: 'starting',
    timeline: '4-8 weeks typical',
    cta: 'Get a Quote',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=300&fit=crop' 
  },
  { 
    icon: Smartphone, 
    title: 'Mobile App Development', 
    desc: 'Beautiful, performant mobile applications for iOS and Android that users love and businesses rely on.', 
    features: [
      'Native iOS (Swift) & Android (Kotlin) apps',
      'Cross-platform (React Native, Flutter)',
      'App store submission & optimization',
      'Push notifications & in-app messaging',
      'Offline-first architecture & data sync'
    ], 
    priceUSD: 20000,
    priceType: 'starting',
    timeline: '10-16 weeks typical',
    cta: 'Get a Quote',
    image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=400&h=300&fit=crop' 
  },
  { 
    icon: Wrench, 
    title: 'Maintenance & Support', 
    desc: 'Keep your applications secure, fast, and up-to-date with our comprehensive maintenance packages.', 
    features: [
      'System monitoring & alerts',
      'Regular security patches & updates',
      'Performance optimization & scaling',
      'Bug fixes & troubleshooting with agreed response times',
      'Feature enhancements & improvements'
    ], 
    priceUSD: 1500,
    priceINR: 125000, // ₹1.25L
    priceType: 'monthly',
    timeline: 'Ongoing support',
    cta: 'Get a Quote',
    image: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=400&h=300&fit=crop' 
  },
  { 
    icon: Users, 
    title: 'Staff Augmentation', 
    desc: 'Scale your team with dedicated developers who work as an extension of your in-house team.', 
    features: [
      'Pre-vetted senior developers',
      'Full-time or part-time engagement',
      'Seamless integration with your workflows',
      'No recruitment costs or overhead',
      'Flexible contracts (monthly, quarterly)'
    ], 
    priceUSD: 3500,
    priceINR: 145000, // ₹1.45L (starting from junior)
    priceType: 'monthly-dev',
    timeline: '2-week onboarding',
    cta: 'Get a Quote',
    image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=400&h=300&fit=crop' 
  }
];

const domains = [
  { 
    icon: Heart, 
    title: 'Healthcare & Telemedicine', 
    subtitle: 'Healthcare software designed for HIPAA requirements',
    gradient: 'from-purple-600 to-fuchsia-500', 
    image: 'https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=400&h=300&fit=crop',
    features: [
      'Patient management systems',
      'Telemedicine platforms with video consultation',
      'EHR/EMR integration',
      'Appointment scheduling systems'
    ],
    cta: 'Get a Quote'
  },
  { 
    icon: ShoppingCart, 
    title: 'Retail & E-Commerce', 
    subtitle: 'Scalable solutions that drive conversions and growth',
    gradient: 'from-purple-600 to-fuchsia-500', 
    image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400&h=300&fit=crop',
    features: [
      'Custom e-commerce platforms (headless commerce)',
      'Multi-vendor marketplaces',
      'Inventory management systems',
      'POS system integration'
    ],
    cta: 'Get a Quote'
  },
  { 
    icon: Banknote, 
    title: 'FinTech & Payments', 
    subtitle: 'Secure financial technology solutions',
    gradient: 'from-purple-600 to-fuchsia-500', 
    image: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=400&h=300&fit=crop',
    features: [
      'Payment gateway integrations',
      'Wallet applications',
      'Peer-to-peer payment systems',
      'Lending platforms'
    ],
    cta: 'Get a Quote'
  },
  { 
    icon: Layers, 
    title: 'SaaS & Subscription', 
    subtitle: 'Build and scale your software-as-a-service product',
    gradient: 'from-purple-600 to-fuchsia-500', 
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=300&fit=crop',
    features: [
      'Multi-tenant architecture',
      'Subscription billing (Stripe, Chargebee)',
      'User authentication & permissions',
      'API development & documentation'
    ],
    cta: 'Get a Quote'
  }
];

const processSteps = [
  { 
    num: '01', 
    title: 'Discovery & Planning', 
    timeline: 'Week 1-2',
    icon: Search,
    activities: [
      'Detailed requirements gathering',
      'Technical architecture planning',
      'Project timeline & milestone definition',
      'Risk assessment & mitigation strategy',
      'NDA signing & IP protection setup'
    ],
    deliverables: 'Requirements document, Technical specification, Project roadmap'
  },
  { 
    num: '02', 
    title: 'Design & Prototyping', 
    timeline: 'Week 2-3',
    icon: PenTool,
    activities: [
      'UI/UX wireframes & mockups',
      'Database schema design',
      'API architecture design',
      'Interactive prototypes for feedback',
      'Design system creation'
    ],
    deliverables: 'Figma designs, Technical architecture document, Approved prototype'
  },
  { 
    num: '03', 
    title: 'Agile Development', 
    timeline: 'Week 4-10',
    icon: Zap,
    activities: [
      '2-week sprint cycles',
      'Daily standup meetings',
      'Continuous code review',
      'Automated testing & QA',
      'Weekly demo sessions with client'
    ],
    deliverables: 'Working software increments, Sprint reports, Test coverage reports'
  },
  { 
    num: '04', 
    title: 'Testing & Deployment', 
    timeline: 'Week 11-12',
    icon: ShieldCheck,
    activities: [
      'Comprehensive QA testing',
      'Performance optimization',
      'Security review',
      'Staging environment deployment',
      'Production deployment & monitoring'
    ],
    deliverables: 'Test reports, Deployment documentation, Performance benchmarks'
  },
  { 
    num: '05', 
    title: 'Launch & Support', 
    timeline: 'Ongoing',
    icon: Rocket,
    activities: [
      'Post-launch monitoring (30 days included)',
      'Bug fixes & optimization',
      'User training & documentation',
      'Maintenance & updates available',
      'Feature enhancement planning'
    ],
    deliverables: 'Support documentation, Performance reports, Enhancement roadmap'
  }
];

const whyChooseUs = [
  { icon: Rocket, title: 'Innovation Partner', desc: 'Bringing innovative thinking in product development including AI-first strategy' },
  { icon: Shield, title: 'End to End Solution', desc: 'From idea conceptualization to deployment and post-launch support' },
  { icon: Award, title: 'Domain Expertise', desc: 'Subject matter experts for target industries' },
  { icon: CheckCircle, title: 'Compliance-Aware', desc: 'We design with GDPR and HIPAA requirements in mind from day one' },
  { icon: Users, title: 'Agile Teams', desc: 'Quickly scale teams based on your needs' },
  { icon: TrendingUp, title: 'Founder-Led Delivery', desc: 'You work directly with the people building your product' }
];

// Dynamic Taglines with unique headlines
// Hero: rotating headlines. Each one is a complete, benefit-led line.
// Keep the strongest first: most visitors only see the first one.
const heroBadge = "Founder-Led • 35+ Years of Leadership Experience";
const taglines = [
  { line1: 'Turn Your Idea Into', line2: 'a Product People Love' },
  { line1: 'Launch Your MVP in', line2: 'Weeks, Not Months' },
  { line1: 'Build Smarter Products', line2: 'With AI That Works' },
  { line1: 'Software That Scales', line2: 'As Fast as You Grow' },
  { line1: 'Your Senior Tech Team,', line2: 'Without the Hiring' }
];
const heroSubtext = "We design, build and launch web, mobile and AI software for startups and growing businesses worldwide. Work directly with the founders, see progress in weekly demos, and own 100% of your code.";

function App({ location } = {}) {
  // During the build-time prerender there is no browser, so use a static router for the given path.
  const Router = location ? StaticRouter : BrowserRouter;
  const routerProps = location ? { location } : {};
  // Initialize dark mode based on system preference
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return true;
  });
  const [currentTagline, setCurrentTagline] = useState(0);
  const [isTaglineFading, setIsTaglineFading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({ 
    name: '', 
    email: '', 
    phone: '',
    countryCode: '+91',
    company: '', 
    website: '',
    projectType: '',
    budget: '',
    timeline: '',
    message: '',
    agreeNda: false,
    wantGuide: false
  });
  const [formStatus, setFormStatus] = useState('idle'); // idle | sending | sent | error
  const [currency, setCurrency] = useState({ code: 'USD', symbol: '$', rate: 1 }); // Default to USD

  const bgClass = darkMode ? 'bg-gray-950' : 'bg-white';
  const textClass = darkMode ? 'text-gray-50' : 'text-gray-900';
  const mutedClass = darkMode ? 'text-gray-400' : 'text-gray-600';
  const cardClass = darkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200';
  const borderClass = darkMode ? 'border-gray-800' : 'border-gray-200';

  // Detect user's country and set currency with robust fallback
  useEffect(() => {
    const detectCountry = async () => {
      // Fallback function using browser language/timezone
      const useFallbackDetection = () => {
        const browserLang = navigator.language || navigator.userLanguage || '';
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
        
        // Check if user is likely from India
        const isIndian = 
          browserLang.toLowerCase().includes('in') || // en-IN, hi-IN, etc.
          browserLang === 'hi' || // Hindi
          timezone.includes('Kolkata') || // Asia/Kolkata
          timezone.includes('Calcutta'); // Legacy timezone name
        
        if (isIndian) {
          setCurrency({ code: 'INR', symbol: '₹', rate: 83 });
        } else {
          setCurrency({ code: 'USD', symbol: '$', rate: 1 });
        }
      };

      try {
        // Create an AbortController for timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout

        const response = await fetch('https://ipapi.co/json/', {
          signal: controller.signal,
          headers: { 'Accept': 'application/json' }
        });
        
        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error('API response not ok');
        }

        const data = await response.json();
        
        // Check if we got valid data (ipapi.co might return error objects)
        if (data.error || !data.country_code) {
          throw new Error('Invalid API response');
        }

        if (data.country_code === 'IN') {
          setCurrency({ code: 'INR', symbol: '₹', rate: 83 });
        } else {
          setCurrency({ code: 'USD', symbol: '$', rate: 1 });
        }
      } catch (error) {
        // API failed, blocked, or timed out - use browser-based fallback
        console.log('Geolocation API unavailable, using browser fallback');
        useFallbackDetection();
      }
    };
    detectCountry();
  }, []);

  // Helper function to format price
  const formatPrice = (usdAmount, showRange = false) => {
    if (currency.code === 'INR') {
      const inrAmount = usdAmount * currency.rate;
      if (inrAmount >= 100000) {
        return `₹${(inrAmount / 100000).toFixed(1)}L`;
      }
      return `₹${inrAmount.toLocaleString('en-IN')}`;
    }
    return `$${usdAmount.toLocaleString()}`;
  };

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => setDarkMode(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Rotate headlines every 4.5s with a fade. Pauses on hover; off for reduced-motion users.
  const taglinePaused = useRef(false);
  useEffect(() => {
    if (taglines.length < 2) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const timer = setInterval(() => {
      if (taglinePaused.current) return;
      setIsTaglineFading(true);
      setTimeout(() => {
        setCurrentTagline((prev) => (prev + 1) % taglines.length);
        setIsTaglineFading(false);
      }, 400);
    }, 4500);
    return () => clearInterval(timer);
  }, []);

//   const handleFormSubmit = async (e) => {
//   e.preventDefault();

//   const payload = {
//     name: formData.name,
//     email: formData.email,
//     phone: `${formData.countryCode}${formData.phone}`,
//     company: formData.company,
//     website: formData.website,
//     projectType: formData.projectType,
//     budget: formData.budget,
//     timeline: formData.timeline,
//     message: formData.message,
//     agreeNda: formData.agreeNda,
//     wantGuide: formData.wantGuide,
//   };

//   try {
//     const response = await fetch("YOUR_NEW_SCRIPT_URL", {
//       method: "POST",
//       headers: {
//         "Content-Type": "application/json",
//       },
//       body: JSON.stringify(payload),
//     });

//     if (response.ok) {
//       alert("Form submitted successfully!");
//     } else {
//       alert("Submission failed");
//     }
//   } catch (err) {
//     alert("Network error");
//   }
// };

const handleFormSubmit = async (e) => {
  e.preventDefault();
  setFormStatus('sending');

  const formDataPayload = new FormData();

  formDataPayload.append("name", formData.name);
  formDataPayload.append("email", formData.email);
  formDataPayload.append(
    "phone",
    `${formData.countryCode}${formData.phone}`
  );
  formDataPayload.append("company", formData.company);
  formDataPayload.append("website", formData.website);
  formDataPayload.append("projectType", formData.projectType);
  formDataPayload.append("budget", formData.budget);
  formDataPayload.append("timeline", formData.timeline);
  formDataPayload.append("message", formData.message);
  formDataPayload.append("agreeNda", formData.agreeNda);
  formDataPayload.append("wantGuide", formData.wantGuide);

  try {
    await fetch("https://script.google.com/macros/s/AKfycbzMMFJbUK69QurOl-NecBOONirW4PjPPHSFhk26yl0swOlwqx4DEjAzFncRyCJf6R9S/exec", {
      method: "POST",
      body: formDataPayload
    });

    setFormStatus('sent');
    setFormData((f) => ({ ...f, name: '', email: '', phone: '', company: '', website: '', projectType: '', budget: '', timeline: '', message: '' }));
  } catch (err) {
    setFormStatus('error');
  }
};

  // Home page content - defined as a variable, not a function component
  const homePageContent = (
    <>
      <main>
        {/* Hero Section with Animated Globe */}
        <section className={`pt-32 pb-24 px-6 relative overflow-hidden transition-colors min-h-screen flex items-center`}>
          {/* Animated Background */}
          <div className="absolute inset-0">
            <div className={`absolute inset-0 ${darkMode ? 'bg-gray-950' : 'bg-gray-100'}`}></div>
          </div>
          
          {/* Floating Particles */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {[
              { left: '5%', delay: '0s', dur: '7s' }, { left: '15%', delay: '1s', dur: '8s' }, { left: '25%', delay: '2s', dur: '6s' },
              { left: '35%', delay: '0.5s', dur: '9s' }, { left: '45%', delay: '3s', dur: '7s' }, { left: '55%', delay: '1.5s', dur: '8s' },
              { left: '65%', delay: '4s', dur: '6s' }, { left: '75%', delay: '2.5s', dur: '9s' }, { left: '85%', delay: '5s', dur: '7s' },
              { left: '95%', delay: '3.5s', dur: '8s' }, { left: '10%', delay: '6s', dur: '9s' }, { left: '30%', delay: '7s', dur: '6s' },
              { left: '50%', delay: '4.5s', dur: '8s' }, { left: '70%', delay: '5.5s', dur: '7s' }, { left: '90%', delay: '6.5s', dur: '9s' }
            ].map((p, i) => (
              <div
                key={i}
                className="particle"
                style={{
                  left: p.left,
                  top: '110%',
                  background: `linear-gradient(135deg, ${['#ec4899', '#a855f7', '#f97316'][i % 3]}, transparent)`,
                  animationDelay: p.delay,
                  animationDuration: p.dur
                }}
              />
            ))}
          </div>

          {/* Animated Gradient Orbs */}
          <div className="absolute inset-0 opacity-30 pointer-events-none">
            <div className={`absolute top-20 left-10 w-72 h-72 rounded-full blur-3xl animate-pulse-glow ${darkMode ? 'bg-pink-500' : 'bg-pink-300'}`} />
            <div className={`absolute top-40 right-20 w-64 h-64 rounded-full blur-3xl animate-pulse-glow ${darkMode ? 'bg-purple-500' : 'bg-purple-300'}`} style={{animationDelay: '1s'}} />
            <div className={`absolute bottom-20 right-10 w-96 h-96 rounded-full blur-3xl animate-pulse-glow ${darkMode ? 'bg-orange-500' : 'bg-orange-300'}`} style={{animationDelay: '2s'}} />
          </div>

          <div className="max-w-7xl mx-auto relative z-10 w-full">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left Content - Headline with rotating product word */}
              <div>
                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 ${darkMode ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30' : 'bg-purple-100 text-purple-600'}`}>
                  <Rocket className="w-4 h-4" />
                  <span className="text-sm font-medium">{heroBadge}</span>
                </div>
                <h1
                  className={`text-[2rem] min-h-[3.75em] md:min-h-0 md:text-5xl lg:text-[clamp(2.4rem,3.9vw,3.4rem)] font-bold leading-tight mb-4 transition-all duration-300 ${isTaglineFading ? 'opacity-0 -translate-y-2' : 'opacity-100 translate-y-0'}`}
                  aria-live="polite"
                  onMouseEnter={() => { taglinePaused.current = true; }}
                  onMouseLeave={() => { taglinePaused.current = false; }}
                >
                  <span className="block md:whitespace-nowrap">{taglines[currentTagline].line1}</span>
                  <span className="block md:whitespace-nowrap text-gradient-animated">{taglines[currentTagline].line2}</span>
                </h1>
                {/* Headline indicators */}
                <div className="flex gap-2 mb-6" role="group" aria-label="Choose headline">
                  {taglines.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      aria-label={`Show headline ${i + 1}`}
                      aria-pressed={i === currentTagline}
                      onClick={() => { setIsTaglineFading(true); setTimeout(() => { setCurrentTagline(i); setIsTaglineFading(false); }, 200); }}
                      className={`h-1.5 rounded-full transition-all duration-300 ${i === currentTagline ? 'w-8 bg-gradient-to-r from-pink-500 via-purple-500 to-orange-500' : `w-3 ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-300 hover:bg-gray-400'}`}`}
                    />
                  ))}
                </div>
                <p className={`text-lg md:text-xl mb-8 leading-relaxed ${mutedClass}`}>
                  {heroSubtext}
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <a href="#contact" className="inline-flex items-center justify-center gap-2 px-8 py-4 btn-glow text-white rounded-lg font-semibold">
                    Get a Free Consultation <ArrowRight className="w-5 h-5" />
                  </a>
                  <Link to="/portfolio" className={`inline-flex items-center justify-center gap-2 px-8 py-4 border-2 rounded-lg transition-colors backdrop-blur-sm ${darkMode ? 'border-gray-600 text-gray-200 hover:bg-gray-800/50' : 'border-gray-300 text-gray-700 hover:bg-gray-100/50'}`}>
                    View Our Work <ArrowRight className="w-5 h-5" />
                  </Link>
                </div>
                {/* Trust Line */}
                <div className={`flex flex-wrap items-center gap-4 text-sm ${mutedClass}`}>
                  <div className="flex items-center gap-1">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>On-time delivery guarantee</span>
                  </div>
                  <span className={`hidden sm:inline ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>|</span>
                  <div className="flex items-center gap-1">
                    <Zap className="w-4 h-4 text-orange-500" />
                    <span>2-week project kickoff</span>
                  </div>
                </div>
              </div>

              {/* Right Side - Animated Globe */}
              <div className="hidden lg:flex justify-center items-center relative">
                {/* Rotating Rings */}
                <div className="absolute w-96 h-96">
                  <div className={`absolute inset-0 rounded-full border-2 animate-spin-slow ${darkMode ? 'border-purple-500/30' : 'border-purple-300/50'}`} style={{animationDuration: '20s'}}></div>
                  <div className={`absolute inset-4 rounded-full border animate-spin-slow ${darkMode ? 'border-pink-500/20' : 'border-pink-300/40'}`} style={{animationDuration: '15s', animationDirection: 'reverse'}}></div>
                  <div className={`absolute inset-8 rounded-full border animate-spin-slow ${darkMode ? 'border-orange-500/20' : 'border-orange-300/40'}`} style={{animationDuration: '25s'}}></div>
                </div>
                
                {/* Globe Image */}
                <div className="relative w-80 h-80 animate-float">
                  <div className={`absolute -inset-4 bg-gradient-to-r from-pink-500 via-purple-500 to-orange-500 rounded-full blur-2xl opacity-30 animate-pulse-glow`}></div>
                  <img 
                    src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=600&h=600&fit=crop" 
                    alt="Digital Globe" 
                    className="relative w-full h-full object-cover rounded-full shadow-2xl"
                    style={{
                      maskImage: 'radial-gradient(circle, black 60%, transparent 70%)',
                      WebkitMaskImage: 'radial-gradient(circle, black 60%, transparent 70%)'
                    }}
                  />
                  {/* Orbiting Dots */}
                  <div className="absolute inset-0 animate-spin-slow" style={{animationDuration: '10s'}}>
                    <div className="absolute top-0 left-1/2 w-3 h-3 bg-pink-500 rounded-full shadow-lg shadow-pink-500/50 -translate-x-1/2 -translate-y-1/2"></div>
                  </div>
                  <div className="absolute inset-0 animate-spin-slow" style={{animationDuration: '8s', animationDirection: 'reverse'}}>
                    <div className="absolute bottom-0 left-1/2 w-2 h-2 bg-purple-500 rounded-full shadow-lg shadow-purple-500/50 -translate-x-1/2 translate-y-1/2"></div>
                  </div>
                  <div className="absolute inset-0 animate-spin-slow" style={{animationDuration: '12s'}}>
                    <div className="absolute top-1/2 right-0 w-2 h-2 bg-orange-500 rounded-full shadow-lg shadow-orange-500/50 translate-x-1/2 -translate-y-1/2"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Commitments Section */}
        <section className={`py-14 px-6 border-y transition-colors ${darkMode ? 'bg-gray-900/50' : 'bg-gray-50'} ${borderClass}`}>
          <div className="max-w-7xl mx-auto">
            <p className={`text-center text-xs font-semibold uppercase tracking-widest mb-8 ${mutedClass}`}>What you get with every project</p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {[
                { icon: Users, value: '35+ Years', label: 'Leadership experience across engineering, AI and delivery' },
                { icon: Clock, value: 'On Time', label: 'Delivery guaranteed, or your money back' },
                { icon: Play, value: 'Weekly Demos', label: 'See working software every week, not just reports' },
                { icon: Shield, value: '100% Yours', label: 'Full ownership of your code and IP' }
              ].map((item, i) => (
                <div key={i} className={`p-5 md:p-6 rounded-2xl border ${cardClass}`}>
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500 via-purple-500 to-orange-500 flex items-center justify-center mb-4">
                    <item.icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-xl md:text-2xl font-bold mb-1">{item.value}</div>
                  <div className={`text-sm leading-snug ${mutedClass}`}>{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What We Do Section - Core Services */}
        <section id="services" className={`py-24 px-6 border-t transition-colors ${bgClass} ${borderClass}`}>
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <span className={`inline-block px-4 py-1 rounded-full text-sm font-medium mb-4 ${darkMode ? 'bg-pink-500/10 text-pink-400' : 'bg-pink-100 text-pink-600'}`}>What We Do</span>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">Core Services</h2>
              <p className={`max-w-2xl mx-auto text-lg ${mutedClass}`}>Everything you need to build, launch and run your software</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, i) => (
                <div key={i} className={`group relative overflow-hidden rounded-2xl transition-all hover:scale-[1.02] ${cardClass} border flex flex-col`}>
                  <div className="relative h-40 overflow-hidden">
                    <img src={service.image} alt={service.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                    <div className={`absolute inset-0 bg-gradient-to-t ${darkMode ? 'from-gray-900 via-gray-900/70 to-transparent' : 'from-white via-white/70 to-transparent'}`}></div>
                    <div className="absolute bottom-4 left-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-pink-500 via-purple-500 to-orange-500 rounded-lg flex items-center justify-center shadow-lg">
                        <service.icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <h3 className="text-xl font-bold mb-2">{service.title}</h3>
                    <p className={`mb-4 text-sm leading-relaxed ${mutedClass}`}>{service.desc}</p>
                    <ul className={`space-y-2 text-sm ${mutedClass} flex-1`}>
                      {service.features.map((feature, j) => (
                        <li key={j} className={`${j >= 3 ? 'hidden md:flex' : 'flex'} items-start gap-2`}>
                          <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    {/* Pricing & Timeline */}
                    <div className={`mt-4 pt-4 border-t ${borderClass} flex items-center gap-3 text-xs ${mutedClass}`}>
                      <div className="flex items-center gap-1">
                        <DollarSign className="w-3.5 h-3.5" />
                        <span className="font-medium">
                          {service.priceType === 'starting' && `Starting at ${formatPrice(service.priceUSD)}`}
                          {service.priceType === 'monthly' && (
                            currency.code === 'INR' && service.priceINR 
                              ? `₹${(service.priceINR / 100000).toFixed(2)}L/month`
                              : `${formatPrice(service.priceUSD)}/month`
                          )}
                          {service.priceType === 'monthly-dev' && (
                            currency.code === 'INR' && service.priceINR 
                              ? `From ₹${(service.priceINR / 100000).toFixed(2)}L/dev/month`
                              : `From ${formatPrice(service.priceUSD)}/dev/month`
                          )}
                        </span>
                      </div>
                      <span>|</span>
                      <div className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        <span>{service.timeline}</span>
                      </div>
                    </div>
                    <a href="#contact" className={`mt-4 inline-flex items-center gap-1 text-sm font-semibold transition-colors ${darkMode ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'}`}>
                      {service.cta} <ArrowRight className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* AI & Innovation Spotlight Section */}
        <section className={`py-24 px-6 border-t transition-colors relative overflow-hidden ${darkMode ? 'bg-gradient-to-br from-gray-900 via-purple-950/30 to-gray-900' : 'bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50'} ${borderClass}`}>
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className={`inline-block px-4 py-1 rounded-full text-sm font-medium mb-4 ${darkMode ? 'bg-orange-500/10 text-orange-400' : 'bg-orange-100 text-orange-600'}`}>Spotlight</span>
                <h2 className="text-4xl md:text-5xl font-bold mb-6">Put <span className={darkMode ? 'text-purple-400' : 'text-purple-600'}>AI to Work</span> in Your Business</h2>
                <p className={`text-lg mb-6 ${mutedClass}`}>
                  We help you add AI where it pays off: chatbots and assistants, document processing, forecasting, and LLM-powered product features, built on proven tools like OpenAI, LangChain and Hugging Face.
                </p>
                <ul className="space-y-4 mb-6">
                  {['Support assistants that answer from your own documents', 'Invoice and document data extraction', 'Sales and demand forecasting from your data', 'AI features inside your product: search, summaries, copilots'].map((item, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gradient-to-br from-pink-500 to-purple-500 rounded-lg flex items-center justify-center">
                        <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                      <span className="font-medium">{item}</span>
                    </li>
                  ))}
                </ul>
                <a href="#contact" className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 text-white rounded-lg font-semibold hover:from-pink-600 hover:via-purple-600 hover:to-indigo-600 transition-all shadow-lg hover:shadow-purple-500/25">
                  Get a Free Consultation <ArrowRight className="w-5 h-5" />
                </a>
              </div>
              <div className="relative">
                <div className={`absolute -inset-4 bg-gradient-to-r from-pink-500 via-purple-500 to-orange-500 rounded-2xl blur-2xl opacity-20`}></div>
                <img 
                  src="https://images.unsplash.com/photo-1677442136019-21780ecad995?w=600&h=400&fit=crop" 
                  alt="AI Technology" 
                  className="relative rounded-2xl shadow-2xl w-full"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Domain Expertise Section */}
        <section id="domains" className={`py-24 px-6 border-t transition-colors ${bgClass} ${borderClass}`}>
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <span className={`inline-block px-4 py-1 rounded-full text-sm font-medium mb-4 ${darkMode ? 'bg-purple-500/10 text-purple-400' : 'bg-purple-100 text-purple-600'}`}>Industries</span>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">Domain Expertise</h2>
              <p className={`max-w-2xl mx-auto text-lg ${mutedClass}`}>Industries where we know the workflows, the rules and the integrations</p>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {domains.map((domain, i) => (
                <div key={i} className={`group relative overflow-hidden rounded-2xl transition-all hover:scale-[1.02] ${cardClass} border flex flex-col`}>
                  {/* Header Image */}
                  <div className="relative h-36 overflow-hidden">
                    <img src={domain.image} alt={domain.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                    <div className={`absolute inset-0 bg-gradient-to-t ${domain.gradient} opacity-70`}></div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
                          <domain.icon className="w-5 h-5 text-white" />
                        </div>
                        <h3 className="text-lg font-bold text-white drop-shadow-lg">{domain.title}</h3>
                      </div>
                    </div>
                  </div>
                  {/* Content */}
                  <div className="p-6 flex-1 flex flex-col">
                    <p className={`text-sm mb-4 ${mutedClass}`}>{domain.subtitle}</p>
                    
                    {/* What We've Built */}
                    <div className="mb-4">
                      <p className={`text-xs font-semibold uppercase tracking-wider mb-3 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>What We Build:</p>
                      <ul className={`space-y-1.5 text-sm ${mutedClass} flex-1`}>
                        {domain.features.map((feature, j) => (
                          <li key={j} className="flex items-start gap-2">
                            <span className="text-green-500 mt-0.5 text-xs">•</span>
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    
                    
                    {/* CTA */}
                    <a href="#contact" className={`mt-auto inline-flex items-center gap-1 text-sm font-semibold transition-colors ${darkMode ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'}`}>
                      {domain.cta} <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Adeez Section */}
        <section className={`py-24 px-6 border-t transition-colors ${darkMode ? 'bg-gradient-to-br from-gray-900 via-purple-950/20 to-gray-900' : 'bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50'} ${borderClass}`}>
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <span className={`inline-block px-4 py-1 rounded-full text-sm font-medium mb-4 ${darkMode ? 'bg-pink-500/10 text-pink-400' : 'bg-pink-100 text-pink-600'}`}>Why Choose Us</span>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">Why Businesses Choose <span className={darkMode ? 'text-purple-400' : 'text-purple-600'}>Adeez</span></h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Global Reach */}
              <div className={`p-6 rounded-2xl border transition-all hover:scale-[1.02] ${cardClass}`}>
                <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center mb-4">
                  <Globe className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold mb-3">Global Reach, Personal Touch</h3>
                <p className={`text-sm leading-relaxed ${mutedClass}`}>
                  We work across all major time zones with dedicated overlap hours for US, UK, EU, and UAE clients. Your project manager is available during your business hours.
                </p>
              </div>
              
              {/* IP Protection */}
              <div className={`p-6 rounded-2xl border transition-all hover:scale-[1.02] ${cardClass}`}>
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-violet-500 rounded-xl flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold mb-3">Your IP is Protected</h3>
                <p className={`text-sm leading-relaxed ${mutedClass}`}>
                  <span className={`inline-flex flex-wrap gap-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    <span className={`px-2 py-0.5 rounded text-xs ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>Strict NDAs</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>Code ownership transfer</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>Secure development</span>
                                      </span>
                </p>
              </div>
              
              {/* Transparent Pricing */}
              <div className={`p-6 rounded-2xl border transition-all hover:scale-[1.02] ${cardClass}`}>
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center mb-4">
                  <DollarSign className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold mb-3">Transparent Pricing</h3>
                <p className={`text-sm leading-relaxed ${mutedClass}`}>
                  Published prices, fixed-price projects and milestone-based payments. You know the cost before we start. No hidden fees.
                </p>
              </div>
              
              {/* Faster Time to Market */}
              <div className={`p-6 rounded-2xl border transition-all hover:scale-[1.02] ${cardClass}`}>
                <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center mb-4">
                  <Clock className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold mb-3">On-Time Delivery, Guaranteed</h3>
                <p className={`text-sm leading-relaxed ${mutedClass}`}>
                  <span className="font-semibold text-orange-500">2-week sprints</span> with weekly demos, so you always see progress. On-time delivery guaranteed or your money back, with the terms written into every project agreement.
                </p>
              </div>
              
              {/* Zero Communication Barriers */}
              <div className={`p-6 rounded-2xl border transition-all hover:scale-[1.02] ${cardClass}`}>
                <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-rose-500 rounded-xl flex items-center justify-center mb-4">
                  <MessageCircle className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold mb-3">Zero Communication Barriers</h3>
                <p className={`text-sm leading-relaxed ${mutedClass}`}>
                  <span className={`inline-flex flex-wrap gap-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    <span className={`px-2 py-0.5 rounded text-xs ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>English-fluent team</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>Daily Slack/Discord</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>Weekly video calls</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>Jira/Asana</span>
                  </span>
                </p>
              </div>
              
              {/* Enterprise-Grade Quality */}
              <div className={`p-6 rounded-2xl border transition-all hover:scale-[1.02] ${cardClass}`}>
                <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center mb-4">
                  <Code2 className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold mb-3">Enterprise-Grade Quality</h3>
                <p className={`text-sm leading-relaxed ${mutedClass}`}>
                  Senior developers (<span className="font-semibold text-purple-500">5+ years avg</span> experience) • Code reviews • Automated testing • CI/CD pipelines • Comprehensive documentation
                </p>
              </div>
            </div>
          </div>
        </section>

        

        {/* Leadership Section */}
        <section className={`py-24 px-6 border-t transition-colors ${bgClass} ${borderClass}`}>
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-14">
              <span className={`inline-block px-4 py-1 rounded-full text-sm font-medium mb-4 ${darkMode ? 'bg-purple-500/10 text-purple-400' : 'bg-purple-100 text-purple-600'}`}>Leadership</span>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">The People You&apos;ll <span className={darkMode ? 'text-purple-400' : 'text-purple-600'}>Work With</span></h2>
              <p className={`max-w-2xl mx-auto text-lg ${mutedClass}`}>No account managers in between. The founding team is on every project call.</p>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {[
                { name: 'Kaustubh Garg', role: 'Founder & CEO', photo: '/team/kaustubh-garg.jpg', bio: '6+ years building software, specialising in scalable architecture and AI/ML. Leads technical direction on every project.' },
                { name: 'Peeyush Garg', role: 'Head of Delivery & Sales', photo: '/team/peeyush-garg.jpg', bio: '30+ years managing and delivering products. PMP certified. Makes sure your project ships on time and on budget.' }
              ].map((person) => (
                <div key={person.name} className={`p-8 rounded-2xl border flex flex-col sm:flex-row items-center sm:items-start gap-6 text-center sm:text-left ${cardClass}`}>
                  <img src={person.photo} alt={person.name} loading="lazy" className="w-28 h-28 rounded-full object-cover ring-4 ring-purple-500/30 flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-bold">{person.name}</h3>
                    <p className={`text-sm font-medium mb-3 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`}>{person.role}</p>
                    <p className={`text-sm leading-relaxed ${mutedClass}`}>{person.bio}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center mt-10">
              <Link to="/about" className={`inline-flex items-center gap-2 font-semibold ${darkMode ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'}`}>
                Meet the full team <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* Technology Partners Section */}
        <section className={`py-16 px-6 border-t transition-colors ${darkMode ? 'bg-gray-900/30' : 'bg-gray-50'} ${borderClass}`}>
          <div className="max-w-7xl mx-auto">
            <p className={`text-center mb-8 text-sm font-medium uppercase tracking-wider ${mutedClass}`}>
              Powered by Industry-Leading Technologies
            </p>
            <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
              {[
                { name: 'AWS', color: 'from-orange-500 to-yellow-500' },
                { name: 'Google Cloud', color: 'from-blue-500 to-green-500' },
                { name: 'Azure', color: 'from-blue-600 to-cyan-500' },
                { name: 'MongoDB', color: 'from-green-600 to-green-400' },
                { name: 'PostgreSQL', color: 'from-blue-700 to-blue-500' },
                { name: 'React', color: 'from-cyan-500 to-blue-500' },
                { name: 'Node.js', color: 'from-green-600 to-lime-500' },
                { name: 'Stripe', color: 'from-purple-600 to-indigo-500' },
                { name: 'OpenAI', color: 'from-emerald-500 to-teal-500' }
              ].map((tech, i) => (
                <div 
                  key={i} 
                  className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all hover:scale-105 ${darkMode ? 'bg-gray-800/50 text-gray-300' : 'bg-white text-gray-700 shadow-sm'}`}
                >
                  <span className={`bg-gradient-to-r ${tech.color} bg-clip-text text-transparent`}>{tech.name}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        

        {/* Process Section */}
        <section id="process" className={`py-24 px-6 border-t transition-colors ${bgClass} ${borderClass}`}>
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <span className={`inline-block px-4 py-1 rounded-full text-sm font-medium mb-4 ${darkMode ? 'bg-pink-500/10 text-pink-400' : 'bg-pink-100 text-pink-600'}`}>Process Flow</span>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">How We Bring Your <span className={darkMode ? 'text-purple-400' : 'text-purple-600'}>Vision to Life</span></h2>
              <p className={`max-w-2xl mx-auto text-lg ${mutedClass}`}>A clear 5-phase process, so you always know what happens next</p>
            </div>
            
            {/* Process Steps - Expanded Cards */}
            <div className="space-y-6">
              {processSteps.map((step, i) => (
                <div key={i} className={`group border rounded-2xl overflow-hidden transition-all hover:border-purple-500/50 ${cardClass}`}>
                  <div className="p-6 md:p-8">
                    <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                      {/* Left: Number, Icon, Title */}
                      <div className="lg:w-64 flex-shrink-0">
                        <div className="flex items-center gap-4 mb-3">
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-500 via-purple-500 to-orange-500 flex items-center justify-center"><step.icon className="w-6 h-6 text-white" /></div>
                          <span className="text-3xl font-bold bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 bg-clip-text text-transparent">{step.num}</span>
                        </div>
                        <h3 className="text-xl font-bold mb-1">{step.title}</h3>
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${darkMode ? 'bg-purple-500/20 text-purple-300' : 'bg-purple-100 text-purple-600'}`}>
                          {step.timeline}
                        </span>
                      </div>
                      
                      {/* Middle: Activities */}
                      <div className="flex-1">
                        <p className={`text-xs font-semibold uppercase tracking-wider mb-3 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>What We Do:</p>
                        <ul className="grid sm:grid-cols-2 gap-2">
                          {step.activities.map((activity, j) => (
                            <li key={j} className={`flex items-start gap-2 text-sm ${mutedClass}`}>
                              <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                              <span>{activity}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      {/* Right: Deliverables */}
                      <div className={`lg:w-72 p-4 rounded-xl ${darkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                        <p className={`text-xs font-semibold uppercase tracking-wider mb-2 ${darkMode ? 'text-green-400' : 'text-green-600'}`}>Deliverables</p>
                        <p className={`text-sm ${mutedClass}`}>{step.deliverables}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Timeline Visual */}
            <div className={`mt-16 p-8 rounded-2xl border ${cardClass}`}>
              <h3 className="text-xl font-bold text-center mb-8">Typical Project Timeline</h3>
              <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-4">
                <div className={`flex-1 max-w-xs text-center p-6 rounded-xl ${darkMode ? 'bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-500/30' : 'bg-gradient-to-br from-cyan-50 to-blue-50 border border-cyan-200'}`}>
                  <div className="text-3xl font-bold text-cyan-500 mb-2">4-8 weeks</div>
                  <div className="font-semibold mb-1">MVP Development</div>
                  <div className={`text-sm ${mutedClass}`}>Core features, basic UI, launch-ready</div>
                </div>
                <ArrowRight className={`hidden md:block w-6 h-6 ${mutedClass}`} />
                <div className={`flex-1 max-w-xs text-center p-6 rounded-xl ${darkMode ? 'bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/30' : 'bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200'}`}>
                  <div className="text-3xl font-bold text-purple-500 mb-2">12-20 weeks</div>
                  <div className="font-semibold mb-1">Full Product</div>
                  <div className={`text-sm ${mutedClass}`}>Complete features, polished UI, integrations</div>
                </div>
                <ArrowRight className={`hidden md:block w-6 h-6 ${mutedClass}`} />
                <div className={`flex-1 max-w-xs text-center p-6 rounded-xl ${darkMode ? 'bg-gradient-to-br from-orange-500/10 to-amber-500/10 border border-orange-500/30' : 'bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-200'}`}>
                  <div className="text-3xl font-bold text-orange-500 mb-2">20+ weeks</div>
                  <div className="font-semibold mb-1">Enterprise Solution</div>
                  <div className={`text-sm ${mutedClass}`}>Complex systems, multiple integrations, scale</div>
                </div>
              </div>
              <p className={`text-center mt-6 text-sm ${mutedClass}`}>
                <span className="font-semibold">Typical timeline:</span> 8-12 weeks from kickoff to launch
              </p>
            </div>
          </div>
        </section>

        {/* Contact Form Section */}
        <section id="contact" className={`py-24 px-6 border-t transition-colors ${bgClass} ${borderClass}`}>
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <span className={`inline-block px-4 py-1 rounded-full text-sm font-medium mb-4 ${darkMode ? 'bg-purple-500/10 text-purple-400' : 'bg-purple-100 text-purple-600'}`}>Get In Touch</span>
              <h2 className="text-4xl md:text-5xl font-bold mb-4">Let&apos;s Build Something <span className={darkMode ? 'text-purple-400' : 'text-purple-600'}>Great</span></h2>
              <p className={`text-lg ${mutedClass}`}>Tell us about your project and we'll respond within 4 hours</p>
              <div className={`flex flex-wrap justify-center gap-x-6 gap-y-2 mt-4 text-sm ${mutedClass}`}>
                {['Free consultation', 'NDA on request', 'No commitment'].map((t) => (
                  <span key={t} className="flex items-center gap-1"><CheckCircle className="w-4 h-4 text-green-500" />{t}</span>
                ))}
              </div>
            </div>
            
            <div className={`max-w-4xl mx-auto border p-8 md:p-10 rounded-2xl ${cardClass}`}>
              <form onSubmit={handleFormSubmit} className="space-y-6">
                {/* Row 1: Name & Email */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${mutedClass}`}>Your Name *</label>
                    <input type="text" required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className={`w-full px-4 py-3 rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 ${darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300'}`} placeholder="John Doe" />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${mutedClass}`}>Email *</label>
                    <input type="email" required value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className={`w-full px-4 py-3 rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 ${darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300'}`} placeholder="john@company.com" />
                  </div>
                </div>
                
                {/* Row 2: Phone */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${mutedClass}`}>Phone Number</label>
                  <div className="flex gap-2">
                    <select aria-label="Country code" value={formData.countryCode} onChange={(e) => setFormData({...formData, countryCode: e.target.value})} className={`px-3 py-3 rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 ${darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300'}`}>
                      <option value="+1">🇺🇸 +1</option>
                      <option value="+44">🇬🇧 +44</option>
                      <option value="+91">🇮🇳 +91</option>
                      <option value="+49">🇩🇪 +49</option>
                      <option value="+33">🇫🇷 +33</option>
                      <option value="+61">🇦🇺 +61</option>
                      <option value="+65">🇸🇬 +65</option>
                      <option value="+971">🇦🇪 +971</option>
                    </select>
                    <input type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className={`flex-1 min-w-0 px-4 py-3 rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 ${darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300'}`} placeholder="Phone number" />
                  </div>
                </div>
                
                {/* Row 4: Project Type, Budget, Timeline */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${mutedClass}`}>Project Type <span className={`text-xs ${mutedClass}`}>(optional)</span></label>
                    <select aria-label="Project type" value={formData.projectType} onChange={(e) => setFormData({...formData, projectType: e.target.value})} className={`w-full px-4 py-3 rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 ${darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300'}`}>
                      <option value="">Select type...</option>
                      <option value="Web Application">Web Application</option>
                      <option value="Mobile App">Mobile App (iOS/Android)</option>
                      <option value="AI/ML Solution">AI/ML Solution</option>
                      <option value="Data Analytics">Data Analytics</option>
                      <option value="E-commerce Platform">E-commerce Platform</option>
                      <option value="MVP Development">MVP Development</option>
                      <option value="Staff Augmentation">Staff Augmentation</option>
                      <option value="Maintenance & Support">Maintenance & Support</option>
                      <option value="Not Sure Yet">Not Sure Yet</option>
                    </select>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${mutedClass}`}>Budget Range <span className={`text-xs ${mutedClass}`}>(optional)</span></label>
                    <select aria-label="Budget range" value={formData.budget} onChange={(e) => setFormData({...formData, budget: e.target.value})} className={`w-full px-4 py-3 rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 ${darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300'}`}>
                      <option value="">Select budget...</option>
                      <option value="Under $10,000">Under $10,000</option>
                      <option value="$10,000 - $25,000">$10,000 - $25,000</option>
                      <option value="$25,000 - $50,000">$25,000 - $50,000</option>
                      <option value="$50,000 - $100,000">$50,000 - $100,000</option>
                      <option value="$100,000+">$100,000+</option>
                      <option value="Not Sure Yet">Not Sure Yet</option>
                    </select>
                  </div>
                  
                </div>
                
                {/* Row 5: Project Details */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${mutedClass}`}>What are you building? *</label>
                  <textarea required rows={5} value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} className={`w-full px-4 py-3 rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none ${darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300'}`} placeholder="Tell us about your project goals, technical requirements, and any specific challenges you're facing..." />
                </div>
                
                <p className={`text-sm ${mutedClass}`}>Happy to sign an NDA before you share details. Just mention it in your message.</p>
                
                {/* Submit Buttons */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <a href="https://wa.me/918595474008?text=Hi%2C%20I%27d%20like%20to%20schedule%20a%20call%20about%20a%20project" target="_blank" rel="noopener noreferrer" className={`flex-1 py-4 border-2 rounded-lg flex items-center justify-center gap-2 font-semibold transition-all ${darkMode ? 'border-purple-500 text-purple-400 hover:bg-purple-500/10' : 'border-purple-500 text-purple-600 hover:bg-purple-50'}`}>
                    Chat on WhatsApp
                  </a>
                  <button type="submit" className="flex-1 py-4 bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 text-white rounded-lg flex items-center justify-center gap-2 font-semibold hover:from-pink-600 hover:via-purple-600 hover:to-indigo-600 transition-all shadow-lg hover:shadow-purple-500/25">
                    <Send className="w-5 h-5" /> {formStatus === 'sending' ? 'Sending...' : 'Send Message'}
                  </button>
                </div>
                {formStatus === 'sent' && (
                  <p className="text-sm font-medium text-green-500">Thanks! We've received your message and will get back to you shortly.</p>
                )}
                {formStatus === 'error' && (
                  <p className="text-sm font-medium text-red-500">Something went wrong. Please message us on WhatsApp or email adeez@adeez.co.in.</p>
                )}
              </form>
            </div>
            
            {/* Prefer to talk directly? */}
            <div className="max-w-4xl mx-auto mt-12 text-center">
              <h3 className={`text-lg font-semibold mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Prefer to talk directly?</h3>
              <div className="flex flex-wrap justify-center gap-6">
                <a href="tel:+918595474008" className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${darkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-100 hover:bg-gray-200'}`}>
                  <Phone className="w-4 h-4" />
                  <span>+91 859 5474008</span>
                </a>
                <a href="https://wa.me/918595474008" target="_blank" rel="noopener noreferrer" className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${darkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-100 hover:bg-gray-200'}`}>
                  <MessageCircle className="w-4 h-4" />
                  <span>WhatsApp</span>
                </a>
                <a href="mailto:adeez@adeez.co.in" className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${darkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-100 hover:bg-gray-200'}`}>
                  <Mail className="w-4 h-4" />
                  <span>adeez@adeez.co.in</span>
                </a>
              </div>
              <p className={`mt-4 text-sm ${mutedClass}`}>
                Business Hours: Mon-Fri 9AM-6PM IST (flexible hours for international clients)
              </p>
            </div>
          </div>
        </section>
      </main>
    </>
  );

  // Shared Navigation component
  const Navigation = () => (
    <nav className={`fixed top-0 w-full backdrop-blur-md z-50 border-b transition-colors ${darkMode ? 'bg-gray-950/95 border-gray-800' : 'bg-white/95 border-gray-200'}`}>
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" onClick={() => setMobileMenuOpen(false)}>
          <AdeezLogo className="h-8" darkMode={darkMode} />
        </Link>
        
        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-6">
          <a href="/#services" className={`transition-colors ${darkMode ? 'text-gray-400 hover:text-gray-50' : 'text-gray-600 hover:text-gray-900'}`}>Services</a>
          <a href="/#domains" className={`transition-colors ${darkMode ? 'text-gray-400 hover:text-gray-50' : 'text-gray-600 hover:text-gray-900'}`}>Industries</a>
          <Link to="/portfolio" className={`transition-colors ${darkMode ? 'text-gray-400 hover:text-gray-50' : 'text-gray-600 hover:text-gray-900'}`}>Portfolio</Link>
          <Link to="/pricing" className={`transition-colors ${darkMode ? 'text-gray-400 hover:text-gray-50' : 'text-gray-600 hover:text-gray-900'}`}>Pricing</Link>
          <Link to="/about" className={`transition-colors ${darkMode ? 'text-gray-400 hover:text-gray-50' : 'text-gray-600 hover:text-gray-900'}`}>About</Link>
          
          <div className={`flex items-center gap-3 text-sm ${mutedClass}`}>
            <a href="tel:+918595474008" className="flex items-center gap-1 hover:text-pink-500 transition-colors">
              <Phone className="w-4 h-4" />
              <span>+91 859 5474008</span>
            </a>
          </div>
          
          <a href="/#contact" className="px-6 py-2 bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 text-white rounded-lg font-medium hover:from-pink-600 hover:via-purple-600 hover:to-indigo-600 transition-all shadow-lg hover:shadow-purple-500/25">Free Consultation</a>
        </div>
        
        {/* Mobile Menu Button */}
        <div className="md:hidden flex items-center gap-3">
          <a href="tel:+918595474008" className="p-2 text-pink-500">
            <Phone className="w-5 h-5" />
          </a>
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      
      {/* Mobile Menu Dropdown */}
      {mobileMenuOpen && (
        <div className={`md:hidden border-t ${darkMode ? 'bg-gray-950 border-gray-800' : 'bg-white border-gray-200'}`}>
          <div className="px-6 py-4 space-y-4">
            <Link to="/" onClick={() => setMobileMenuOpen(false)} className={`block py-2 font-medium ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Home</Link>
            <a href="/#services" onClick={() => setMobileMenuOpen(false)} className={`block py-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Services</a>
            <a href="/#domains" onClick={() => setMobileMenuOpen(false)} className={`block py-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Industries</a>
            <Link to="/portfolio" onClick={() => setMobileMenuOpen(false)} className={`block py-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Portfolio</Link>
            <Link to="/pricing" onClick={() => setMobileMenuOpen(false)} className={`block py-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Pricing</Link>
            <Link to="/about" onClick={() => setMobileMenuOpen(false)} className={`block py-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>About</Link>
            
            <div className={`pt-4 border-t ${borderClass}`}>
              <a href="/#contact" onClick={() => setMobileMenuOpen(false)} className="block w-full py-3 text-center bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 text-white rounded-lg font-medium">
                Get a Free Consultation
              </a>
            </div>
            
            <div className={`flex items-center justify-center gap-4 pt-4 ${mutedClass}`}>
              <a href="tel:+918595474008" className="flex items-center gap-2 text-sm">
                <Phone className="w-4 h-4" /> +91 859 5474008
              </a>
              <a href="https://wa.me/918595474008" target="_blank" rel="noopener noreferrer" className="p-2 bg-green-500 rounded-full">
                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );

  // Shared Footer component
  const Footer = () => (
    <footer className={`py-16 px-6 border-t transition-colors ${darkMode ? 'bg-gray-950 text-gray-400' : 'bg-gray-100 text-gray-600'} ${borderClass}`}>
        <div className="max-w-7xl mx-auto">
          {/* Main Footer Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 mb-12">
            {/* Brand */}
            <div className="col-span-2 md:col-span-3 lg:col-span-2">
              <AdeezLogo className="h-8 mb-4" darkMode={darkMode} />
              <p className="text-sm mb-4 max-w-xs">Founder-led software development for startups and growing businesses worldwide.</p>
            </div>
            
            {/* Services */}
            <div>
              <h4 className={`font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Services</h4>
              <ul className="space-y-2 text-sm">
                {['Custom Development', 'AI & ML Solutions', 'Data Analytics', 'Mobile Apps', 'Maintenance', 'Staff Augmentation'].map((item, i) => (
                  <li key={i}><a href="#services" className="hover:text-purple-500 transition-colors">{item}</a></li>
                ))}
              </ul>
            </div>
            
            {/* Industries */}
            <div>
              <h4 className={`font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Industries</h4>
              <ul className="space-y-2 text-sm">
                {['Healthcare', 'Retail & E-Commerce', 'FinTech', 'SaaS Products'].map((item, i) => (
                  <li key={i}><a href="#domains" className="hover:text-purple-500 transition-colors">{item}</a></li>
                ))}
              </ul>
            </div>
            
            {/* Company */}
            <div>
              <h4 className={`font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Company</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/about" className="hover:text-purple-500 transition-colors">About Us</Link></li>
                <li><a href="/#process" className="hover:text-purple-500 transition-colors">Our Process</a></li>
                <li><Link to="/portfolio" className="hover:text-purple-500 transition-colors">Portfolio</Link></li>
                <li><Link to="/pricing" className="hover:text-purple-500 transition-colors">Pricing</Link></li>
                <li><a href="/#contact" className="hover:text-purple-500 transition-colors">Contact Us</a></li>
                <li><a href="#" className="hover:text-purple-500 transition-colors">Privacy Policy</a></li>
              </ul>
            </div>
            
            {/* Contact */}
            <div>
              <h4 className={`font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Contact</h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2"><Mail className="w-4 h-4" /> <span>adeez@adeez.co.in</span></li>
                <li className="flex items-center gap-2"><Phone className="w-4 h-4" /> <span>+91 859 5474008</span></li>
                <li className="flex items-center gap-2"><MessageCircle className="w-4 h-4" /> <a href="https://wa.me/918595474008" className="hover:text-purple-500">WhatsApp</a></li>
                <li className="flex items-center gap-2">📍 <span>Noida, India</span></li>
              </ul>
            </div>
          </div>
          
          {/* Global Presence & Trust */}
          <div className={`py-8 border-y ${borderClass} mb-8`}>
            <div className="grid md:grid-cols-3 gap-8">
              {/* Global Presence */}
              <div>
                <h4 className={`font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Working Hours</h4>
                <p className="text-sm">Overlap hours for US, UK, EU and UAE clients</p>
                <p className={`text-xs mt-2 ${mutedClass}`}>Based in Noida, India (IST). Flexible hours for international projects.</p>
              </div>
              
              {/* Payments */}
              <div>
                <h4 className={`font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Payments Accepted</h4>
                <div className="flex flex-wrap gap-2 text-sm">
                  {['Wire Transfer', 'PayPal', 'Stripe', 'Wise'].map((item, i) => (
                    <span key={i} className={`px-2 py-1 rounded text-xs ${darkMode ? 'bg-gray-800' : 'bg-gray-200'}`}>{item}</span>
                  ))}
                </div>
              </div>
              
              {/* Certifications */}
              <div>
                <h4 className={`font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Trust & Security</h4>
                <div className="flex flex-wrap gap-2 text-sm">
                  {['NDA Protected', 'Full Code Ownership'].map((item, i) => (
                    <span key={i} className={`px-2 py-1 rounded text-xs ${darkMode ? 'bg-gray-800' : 'bg-gray-200'}`}>✓ {item}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
          
          {/* Bottom Bar */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-sm">© {new Date().getFullYear()} Adeez Technologies Private Limited. All rights reserved.</div>
            <div className="flex items-center gap-4">
              {/* Dark/Light Mode Toggle */}
              <button onClick={() => setDarkMode(!darkMode)} className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${darkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-200 hover:bg-gray-300'}`}>
                {darkMode ? <Sun className="w-4 h-4 text-yellow-500" /> : <Moon className="w-4 h-4 text-gray-600" />}
                <span className="text-sm">{darkMode ? 'Light' : 'Dark'}</span>
              </button>
              <div className="text-sm">
                <a href="#" className="hover:text-purple-500 transition-colors">Privacy</a>
                <span className="mx-2">•</span>
                <a href="#" className="hover:text-purple-500 transition-colors">Terms</a>
                <span className="mx-2">•</span>
                <a href="#" className="hover:text-purple-500 transition-colors">Cookies</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
  );

  // WhatsApp floating button
  const WhatsAppButton = () => (
    <a 
      href="https://wa.me/918595474008?text=Hi, I'd like to discuss a project for my business" 
      target="_blank" 
      rel="noopener noreferrer" 
      className="fixed bottom-4 right-4 md:bottom-6 md:right-6 z-50 p-3 md:p-4 bg-green-500 hover:bg-green-600 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-110 group"
    >
      <svg className="w-6 h-6 md:w-7 md:h-7 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
      <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 px-3 py-1 bg-gray-900 text-white text-sm rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
        Chat with us!
      </span>
    </a>
  );

  // Global styles
  const GlobalStyles = () => (
    <style>{`
      @keyframes float { 0%, 100% { transform: translateY(0px) rotate(0deg); } 50% { transform: translateY(-20px) rotate(5deg); } }
      @keyframes pulse-glow { 0%, 100% { opacity: 0.4; transform: scale(1); } 50% { opacity: 0.8; transform: scale(1.1); } }
      @keyframes spin-slow { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      @keyframes gradient-shift { 0% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } 100% { background-position: 0% 50%; } }
      @keyframes float-particle { 0%, 100% { transform: translateY(0) translateX(0); opacity: 0; } 10% { opacity: 1; } 90% { opacity: 1; } 100% { transform: translateY(-100vh) translateX(20px); opacity: 0; } }
      @keyframes shimmer { 0% { background-position: -200% center; } 100% { background-position: 200% center; } }
      @keyframes glow-pulse { 0%, 100% { box-shadow: 0 0 20px rgba(168, 85, 247, 0.4), 0 0 40px rgba(236, 72, 153, 0.2); } 50% { box-shadow: 0 0 30px rgba(168, 85, 247, 0.6), 0 0 60px rgba(236, 72, 153, 0.4); } }
      .animate-float { animation: float 6s ease-in-out infinite; }
      .animate-pulse-glow { animation: pulse-glow 4s ease-in-out infinite; }
      .animate-spin-slow { animation: spin-slow 20s linear infinite; }
      .animate-gradient { background-size: 200% 200%; animation: gradient-shift 8s ease infinite; }
      .particle { position: absolute; width: 4px; height: 4px; border-radius: 50%; animation: float-particle 8s ease-in-out infinite; }
      .btn-glow { 
        background: linear-gradient(135deg, #ec4899 0%, #a855f7 25%, #6366f1 50%, #a855f7 75%, #ec4899 100%);
        background-size: 300% 100%;
        animation: shimmer 8s ease-in-out infinite, glow-pulse 4s ease-in-out infinite;
        transition: all 0.3s ease;
      }
      .btn-glow:hover {
        transform: translateY(-2px);
        filter: brightness(1.1);
      }
      .text-gradient-animated {
        background: linear-gradient(135deg, #ec4899 0%, #a855f7 25%, #6366f1 50%, #f97316 75%, #ec4899 100%);
        background-size: 300% 100%;
        -webkit-background-clip: text;
        background-clip: text;
        color: transparent;
        animation: shimmer 10s ease-in-out infinite;
      }
      @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      .animate-fadeIn { animation: fadeIn 0.3s ease-out; }
      
      /* Lazy loading animations */
      @keyframes fadeInUp { 
        from { opacity: 0; transform: translateY(30px); } 
        to { opacity: 1; transform: translateY(0); } 
      }
      .fade-in-section {
        opacity: 0;
        transform: translateY(30px);
        transition: opacity 0.6s ease-out, transform 0.6s ease-out;
      }
      .fade-in-section.visible {
        opacity: 1;
        transform: translateY(0);
      }
      
      /* Skeleton loading */
      @keyframes skeleton-pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.5; }
      }
      .skeleton {
        animation: skeleton-pulse 1.5s ease-in-out infinite;
        background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
        background-size: 200% 100%;
      }
      .dark .skeleton {
        background: linear-gradient(90deg, #374151 25%, #4b5563 50%, #374151 75%);
        background-size: 200% 100%;
      }
      
      /* Image loading placeholder */
      .img-loading {
        background: linear-gradient(90deg, #e0e0e0 0%, #f5f5f5 50%, #e0e0e0 100%);
        background-size: 200% 100%;
        animation: shimmer 1.5s infinite;
      }
      .dark .img-loading {
        background: linear-gradient(90deg, #374151 0%, #4b5563 50%, #374151 100%);
      }
    `}</style>
  );

  return (
    <Router {...routerProps}>
      <ScrollToTop />
      <PageMeta />
      <div className={`min-h-screen ${bgClass} ${textClass}`}>
        <GlobalStyles />
        <Navigation />
        <WhatsAppButton />
        
        <Suspense fallback={<PageLoader />}>
        <Routes>
            <Route path="/" element={homePageContent} />
          <Route path="/about" element={<About darkMode={darkMode} bgClass={bgClass} textClass={textClass} mutedClass={mutedClass} cardClass={cardClass} borderClass={borderClass} />} />
          <Route path="/portfolio" element={<Portfolio darkMode={darkMode} bgClass={bgClass} textClass={textClass} mutedClass={mutedClass} cardClass={cardClass} borderClass={borderClass} />} />
          <Route path="/pricing" element={<Pricing darkMode={darkMode} bgClass={bgClass} textClass={textClass} mutedClass={mutedClass} cardClass={cardClass} borderClass={borderClass} currency={currency} formatPrice={formatPrice} />} />
        </Routes>
        </Suspense>
        
        <Footer />
      </div>
    </Router>
  );
}

export default App;
